import { useEffect, useRef, useState } from "react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

export default function Location() {
  const { ref, inView } = useInView();

  return (
    <section id="ubicacion" className="py-20 md:py-32 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className={`text-center mb-12 md:mb-16 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase">Encuéntranos</span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0A1F44] mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Sucursales y Ubicación
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto">
            Visítanos en nuestras instalaciones en Zapopan, Jalisco, o conéctate desde cualquier parte del mundo en nuestros programas online.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Info Card */}
          <div className={`transition-all duration-700 delay-100 ${inView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
            <div className="bg-[#F8F6F0] rounded-2xl p-8 md:p-10 border border-[#D4AF37]/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0A1F44] text-[#D4AF37]">
                  <i className="ri-map-pin-2-line text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1F44]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Sede Principal
                  </h3>
                  <span className="text-[#D4AF37] text-sm font-medium">Zapopan, Jalisco</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-[#D4AF37]">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 font-medium uppercase tracking-wide mb-1">Dirección</div>
                    <div className="text-[#0A1F44] font-semibold">Juan Kepler 4052A</div>
                    <div className="text-gray-600">Colonia Arboledas</div>
                    <div className="text-gray-600">Zapopan, Jalisco, CP 45070</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-[#D4AF37]">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 font-medium uppercase tracking-wide mb-1">Teléfono / WhatsApp</div>
                    <a
                      href="https://wa.me/523325077743"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0A1F44] font-semibold hover:text-[#D4AF37] transition-colors cursor-pointer"
                    >
                      +52 33 2507 7743
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-[#D4AF37]">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 font-medium uppercase tracking-wide mb-1">Horarios</div>
                    <div className="text-[#0A1F44] font-semibold">Lunes a Viernes: 9:00 - 19:00</div>
                    <div className="text-gray-600">Sábados: 9:00 - 14:00</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-[#D4AF37]">
                    <i className="ri-car-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 font-medium uppercase tracking-wide mb-1">Accesibilidad</div>
                    <div className="text-gray-600">Estacionamiento disponible · Acceso para personas con discapacidad · Cerca de transporte público</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://maps.google.com/?q=Juan+Kepler+4052A+Arboledas+Zapopan+Jalisco"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#0A1F44] text-white font-semibold text-sm px-6 py-3 rounded-full hover:bg-[#0d2a5e] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-map-pin-line"></i>
                  Ver en Google Maps
                </a>
                <a
                  href="https://wa.me/523325077743?text=Hola,%20quiero%20agendar%20una%20visita%20al%20Instituto%20Ashaika"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 border-2 border-[#D4AF37] text-[#D4AF37] font-semibold text-sm px-6 py-3 rounded-full hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>
                  Agendar Visita
                </a>
              </div>
            </div>

            {/* Online badge */}
            <div className="mt-5 bg-[#0A1F44] rounded-xl p-5 flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#D4AF37]/20 text-[#D4AF37] flex-shrink-0">
                <i className="ri-global-line text-xl"></i>
              </div>
              <div>
                <div className="text-white font-bold">También disponible Online</div>
                <div className="text-white/60 text-sm">Cursos y sesiones terapéuticas desde cualquier parte del mundo</div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className={`transition-all duration-700 delay-200 ${inView ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
            <div className="rounded-2xl overflow-hidden border border-[#D4AF37]/20 h-[400px] md:h-[520px]">
              <iframe
                title="Instituto Ashaika - Ubicación"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3731.5!2d-103.4!3d20.7!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sJuan+Kepler+4052A%2C+Arboledas%2C+Zapopan%2C+Jalisco+45070!5e0!3m2!1ses!2smx!4v1700000000000!5m2!1ses!2smx"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
